import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  ChevronLeft, Plus, Trash2, GripVertical, Save,
  Zap, ArrowRight, Play, Settings, X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  TRIGGER_TYPES, ACTION_TYPES, AUTOMATION_CATEGORIES,
  AUTOMATION_TYPES, CONDITION_OPERATORS, CONDITION_FIELDS,
  TEMPLATE_VARIABLES
} from './automationConfig';

export const AutomationBuilder = ({ automation, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: automation?.name || '',
    description: automation?.description || '',
    category: automation?.category || 'sales',
    automation_type: automation?.automation_type || 'standard',
    status: automation?.status || 'draft',
    triggers: automation?.triggers || [],
    condition_groups: automation?.condition_groups || [],
    actions: automation?.actions || []
  });

  const [activeTab, setActiveTab] = useState('triggers');
  const [isSaving, setIsSaving] = useState(false);

  // Add trigger
  const addTrigger = (type) => {
    const newTrigger = {
      id: `trigger-${Date.now()}`,
      type,
      event_filters: {},
      keywords: [],
      business_hours_only: false
    };
    setForm(prev => ({
      ...prev,
      triggers: [...prev.triggers, newTrigger]
    }));
  };

  // Remove trigger
  const removeTrigger = (triggerId) => {
    setForm(prev => ({
      ...prev,
      triggers: prev.triggers.filter(t => t.id !== triggerId)
    }));
  };

  // Update trigger
  const updateTrigger = (triggerId, updates) => {
    setForm(prev => ({
      ...prev,
      triggers: prev.triggers.map(t =>
        t.id === triggerId ? { ...t, ...updates } : t
      )
    }));
  };

  // Add action
  const addAction = (type) => {
    const newAction = {
      id: `action-${Date.now()}`,
      type,
      order: form.actions.length,
      config: {},
      message_template: '',
      retry_on_failure: false
    };
    setForm(prev => ({
      ...prev,
      actions: [...prev.actions, newAction]
    }));
  };

  // Remove action
  const removeAction = (actionId) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.filter(a => a.id !== actionId)
    }));
  };

  // Update action
  const updateAction = (actionId, updates) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map(a =>
        a.id === actionId ? { ...a, ...updates } : a
      )
    }));
  };

  // Add condition group
  const addConditionGroup = () => {
    const newGroup = {
      id: `group-${Date.now()}`,
      logic: 'and',
      conditions: [{
        id: `cond-${Date.now()}`,
        field: '',
        operator: 'equals',
        value: ''
      }]
    };
    setForm(prev => ({
      ...prev,
      condition_groups: [...prev.condition_groups, newGroup]
    }));
  };

  // Handle save
  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Please enter an automation name');
      return;
    }
    if (form.triggers.length === 0) {
      alert('Please add at least one trigger');
      return;
    }
    if (form.actions.length === 0) {
      alert('Please add at least one action');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(form);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'triggers', label: 'Triggers', count: form.triggers.length },
    { id: 'conditions', label: 'Conditions', count: form.condition_groups.length },
    { id: 'actions', label: 'Actions', count: form.actions.length }
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">
              {automation?.id ? 'Edit Automation' : 'Create Automation'}
            </h1>
            <p className="text-sm text-muted-foreground">Build your automation workflow</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={form.status}
            onValueChange={(val) => setForm(prev => ({ ...prev, status: val }))}
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Automation'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Settings */}
        <div className="w-80 border-r p-4 overflow-y-auto">
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="My Automation"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What does this automation do?"
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label>Category</Label>
              <Select
                value={form.category}
                onValueChange={(val) => setForm(prev => ({ ...prev, category: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTOMATION_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Type</Label>
              <Select
                value={form.automation_type}
                onValueChange={(val) => setForm(prev => ({ ...prev, automation_type: val }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTOMATION_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Template Variables Reference */}
            <div>
              <Label className="text-xs text-muted-foreground">Template Variables</Label>
              <div className="mt-2 space-y-1">
                {TEMPLATE_VARIABLES.slice(0, 6).map(v => (
                  <code key={v.value} className="block text-xs bg-muted px-2 py-1 rounded">
                    {v.value}
                  </code>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Center Panel - Builder */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b px-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
                  activeTab === tab.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {tab.label}
                {tab.count > 0 && (
                  <Badge variant="secondary" className="ml-2">{tab.count}</Badge>
                )}
              </button>
            ))}
          </div>

          <ScrollArea className="flex-1 p-4">
            {/* Triggers Tab */}
            {activeTab === 'triggers' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">When this happens...</h3>
                  <Select onValueChange={addTrigger}>
                    <SelectTrigger className="w-[200px]">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Trigger
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(TRIGGER_TYPES).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {config.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {form.triggers.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <Zap className="h-8 w-8 mb-2 opacity-50" />
                      <p>No triggers added yet</p>
                      <p className="text-sm">Add a trigger to start your automation</p>
                    </CardContent>
                  </Card>
                ) : (
                  form.triggers.map((trigger, idx) => {
                    const config = TRIGGER_TYPES[trigger.type];
                    const Icon = config?.icon || Zap;
                    return (
                      <Card key={trigger.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Badge className={config?.color}>
                                <Icon className="h-3 w-3 mr-1" />
                                {config?.label || trigger.type}
                              </Badge>
                            </CardTitle>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => removeTrigger(trigger.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Trigger-specific config */}
                          {trigger.type === 'keyword_detected' && (
                            <div>
                              <Label className="text-xs">Keywords (comma-separated)</Label>
                              <Input
                                value={trigger.keywords?.join(', ') || ''}
                                onChange={(e) => updateTrigger(trigger.id, {
                                  keywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean)
                                })}
                                placeholder="quote, estimate, price"
                                className="mt-1"
                              />
                            </div>
                          )}
                          {trigger.type === 'job_stage_changed' && (
                            <div>
                              <Label className="text-xs">To Stage</Label>
                              <Input
                                value={trigger.event_filters?.to_stage || ''}
                                onChange={(e) => updateTrigger(trigger.id, {
                                  event_filters: { ...trigger.event_filters, to_stage: e.target.value }
                                })}
                                placeholder="scheduling"
                                className="mt-1"
                              />
                            </div>
                          )}
                          {(trigger.type === 'scheduled' || trigger.type === 'recurring') && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Time</Label>
                                <Input
                                  type="time"
                                  value={trigger.schedule_time || '09:00'}
                                  onChange={(e) => updateTrigger(trigger.id, { schedule_time: e.target.value })}
                                  className="mt-1"
                                />
                              </div>
                              {trigger.type === 'scheduled' && (
                                <div>
                                  <Label className="text-xs">Date</Label>
                                  <Input
                                    type="date"
                                    value={trigger.schedule_date || ''}
                                    onChange={(e) => updateTrigger(trigger.id, { schedule_date: e.target.value })}
                                    className="mt-1"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}

            {/* Conditions Tab */}
            {activeTab === 'conditions' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Only if these conditions are met... (optional)</h3>
                  <Button variant="outline" size="sm" onClick={addConditionGroup}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Condition Group
                  </Button>
                </div>

                {form.condition_groups.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <Settings className="h-8 w-8 mb-2 opacity-50" />
                      <p>No conditions added</p>
                      <p className="text-sm">Automation will run for all triggers</p>
                    </CardContent>
                  </Card>
                ) : (
                  form.condition_groups.map((group, idx) => (
                    <Card key={group.id}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm">Condition Group {idx + 1}</CardTitle>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setForm(prev => ({
                              ...prev,
                              condition_groups: prev.condition_groups.filter(g => g.id !== group.id)
                            }))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Select
                          value={group.logic}
                          onValueChange={(val) => {
                            setForm(prev => ({
                              ...prev,
                              condition_groups: prev.condition_groups.map(g =>
                                g.id === group.id ? { ...g, logic: val } : g
                              )
                            }));
                          }}
                        >
                          <SelectTrigger className="w-[100px] mb-3">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="and">AND</SelectItem>
                            <SelectItem value="or">OR</SelectItem>
                          </SelectContent>
                        </Select>
                        {/* Condition rows would go here */}
                        <p className="text-sm text-muted-foreground">Condition builder coming soon...</p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Actions Tab */}
            {activeTab === 'actions' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Then do this...</h3>
                  <Select onValueChange={addAction}>
                    <SelectTrigger className="w-[200px]">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Action
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACTION_TYPES).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {config.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {form.actions.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <ArrowRight className="h-8 w-8 mb-2 opacity-50" />
                      <p>No actions added yet</p>
                      <p className="text-sm">Add actions to define what happens</p>
                    </CardContent>
                  </Card>
                ) : (
                  form.actions.map((action, idx) => {
                    const config = ACTION_TYPES[action.type];
                    const Icon = config?.icon || Zap;
                    return (
                      <Card key={action.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <span className="text-muted-foreground">#{idx + 1}</span>
                              <Badge className={config?.color}>
                                <Icon className="h-3 w-3 mr-1" />
                                {config?.label || action.type}
                              </Badge>
                            </CardTitle>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => removeAction(action.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Message actions */}
                          {['send_sms', 'send_email', 'send_message'].includes(action.type) && (
                            <div>
                              <Label className="text-xs">Message Template</Label>
                              <Textarea
                                value={action.message_template || ''}
                                onChange={(e) => updateAction(action.id, { message_template: e.target.value })}
                                placeholder="Hi {{contact.first_name}}, ..."
                                className="mt-1"
                                rows={3}
                              />
                            </div>
                          )}
                          {/* Task actions */}
                          {action.type === 'create_task' && (
                            <>
                              <div>
                                <Label className="text-xs">Task Title</Label>
                                <Input
                                  value={action.task_title || ''}
                                  onChange={(e) => updateAction(action.id, { task_title: e.target.value })}
                                  placeholder="Follow up with customer"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Due in (days)</Label>
                                <Input
                                  type="number"
                                  value={action.task_due_days || ''}
                                  onChange={(e) => updateAction(action.id, { task_due_days: parseInt(e.target.value) || null })}
                                  placeholder="3"
                                  className="mt-1 w-24"
                                />
                              </div>
                            </>
                          )}
                          {/* Delay action */}
                          {action.type === 'delay' && (
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <Label className="text-xs">Days</Label>
                                <Input
                                  type="number"
                                  value={action.config?.days || ''}
                                  onChange={(e) => updateAction(action.id, {
                                    config: { ...action.config, days: parseInt(e.target.value) || 0 }
                                  })}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Hours</Label>
                                <Input
                                  type="number"
                                  value={action.config?.hours || ''}
                                  onChange={(e) => updateAction(action.id, {
                                    config: { ...action.config, hours: parseInt(e.target.value) || 0 }
                                  })}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Minutes</Label>
                                <Input
                                  type="number"
                                  value={action.config?.minutes || ''}
                                  onChange={(e) => updateAction(action.id, {
                                    config: { ...action.config, minutes: parseInt(e.target.value) || 0 }
                                  })}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          )}
                          {/* Wait for reply */}
                          {action.type === 'wait_for_reply' && (
                            <div>
                              <Label className="text-xs">Timeout (minutes)</Label>
                              <Input
                                type="number"
                                value={action.wait_timeout_minutes || 60}
                                onChange={(e) => updateAction(action.id, { wait_timeout_minutes: parseInt(e.target.value) || 60 })}
                                className="mt-1 w-32"
                              />
                            </div>
                          )}
                          {/* Workflow stage */}
                          {action.type === 'change_workflow_stage' && (
                            <div>
                              <Label className="text-xs">New Stage</Label>
                              <Input
                                value={action.config?.stage || ''}
                                onChange={(e) => updateAction(action.id, {
                                  config: { ...action.config, stage: e.target.value }
                                })}
                                placeholder="scheduling"
                                className="mt-1"
                              />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
